/**
 * FAQs for /langy, exported separately so routes.tsx can build the FAQPage
 * JSON-LD without pulling the page chunk into the route table.
 *
 * Every answer here restates something already published in the Langy launch
 * post (/blog/introducing-langy-your-automated-ai-engineer). Keep it that way:
 * this page markets a product that exists, so a new claim belongs in the post
 * first. Voice: plain and technical, see docs/design-system/copy.md.
 */
export const langyFaq: { q: string; a: string }[] = [
  {
    q: 'What is an automated AI engineer?',
    a: 'A coding agent embedded in the platform where your AI product is observed and tested, doing real engineering work on it: agent evaluation and testing, trace analysis, prompt and code changes, pull requests for your team to review. A copilot helps a person type and a chat assistant answers questions about your dashboards. An automated AI engineer does the work and shows the diff.',
  },
  {
    q: 'What does Langy actually do for my team?',
    a: 'The work you already do when improving an agent, automated: digging up the trace behind a complaint, figuring out what went wrong, writing the evaluation or the scenario test that catches it, fixing the prompt, checking the fix held. You ask in plain language, and Langy comes back with findings, or with a pull request.',
  },
  {
    q: 'Can Langy improve my AI agent automatically?',
    a: 'Up to the pull request, yes. Langy finds the problems, writes the scenario tests and evaluations, drafts the fix, and opens the PR with the CI results attached. Merging stays with your team, so you get the speed of automation with a human review on every change that ships.',
  },
  {
    q: 'How do I test AI agents with Langy?',
    a: 'Tell it what the agent should and should not do. Langy turns that into Scenario simulations, multi-turn conversations that probe your agent the way a real user would, plus evaluations that score the outcomes. The tests run in your CI, so every future change gets checked against them, whoever wrote it.',
  },
  {
    q: 'Can Langy test voice agents?',
    a: 'Yes. Our launch demo is exactly that: Langy, plugged into a robot over MCP, testing our own customer-support voice agent by kicking off Scenario simulations itself. Voice agent testing works the same way as text, with simulated conversations scored against the rubric you set.',
  },
  {
    q: 'How does Langy spend tokens, and whose account does it use?',
    a: 'Yours. You bring your own API key for the model provider you already use, or run Langy on a coding-agent subscription you already pay for, like a Codex account. Every Langy session is itself traced and costed inside LangWatch, so you see exactly what each run spent.',
  },
  {
    q: 'Is it safe to give an AI engineer access to my code and data?',
    a: 'Langy runs in a fully sandboxed VM and operates only on your LangWatch account for anything sensitive, so credentials stay there. Secret redaction is on by default, so secrets do not leak into context. On the code side, Langy contributes to your GitHub exclusively through pull requests, so nothing touches your codebase without a human approving it.',
  },
  {
    q: 'Which frameworks does Langy work with?',
    a: 'Any agent framework or harness. LangWatch is OpenTelemetry-native, so if LangWatch traces it, Langy can work on it. Models from any provider too, including OpenAI, Anthropic, Bedrock, Azure, and ElevenLabs, on your own keys.',
  },
]
